import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import classnames from 'classnames';
import { FormGroup, Col } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm } from 'redux-form';

import TrustPilotBadge from '@src/components/TrustPilotBadge/TrustPilotBadge';

import { businessDetailsFormValidation } from './businessDetailsSchema';
import messages from './BusinessIntroduction.messages';
import { BusinessStartDateQuestion } from './BusinessStartDateQuestion/BusinessStartDateQuestion';
import { limitedCompanyBusinessTypeFields } from './fields';
import styles from './LimitedCompanyFields.module.css';
import TurnoverQuestion from './TurnoverQuestion';
import schemaValidator from './validate';
import { VATQuestion } from './VATQuestion';
import { WebsiteQuestion } from './WebsiteQuestion';
import { toggleManualCompanyName } from '../../actions';
import AutoCompany from '../../components/CompanyName/AutoCompany/AutoCompany';
import ManualCompany from '../../components/CompanyName/ManualCompany';
import SelectLive from '../../components/DropDown/SelectLive';
import FinishStepButton from '../../components/FinishStepButton/FinishStepButton';
import { resetDirectors } from '../../components/FullName/limitedCompany/AutoDirectors.actions';
import { getAgeFromDateInMonths, splitISODate } from '../../helpers/dates';
import { dateFormatter, nonAlphaNumeric } from '../../helpers/helpers';
import normalizer from '../../helpers/inputNormalizer';
import { scrollToErrors } from '../../helpers/scrollToErrors';
import {
    getCountryCode,
    hasAutomatedDecision as hasAutomatedDecisionSelector,
    isDirectUKCustomer
} from '../../lendingAPI/lendingAPISelector';
import { getSplitTestValue } from '../../utils/selectors';

const addLeadingZero = (string) => {
    const length = string.length;

    if (length === 7 && /\d{7}/.test(string)) {
        return `0${string}`;
    }

    return string;
};

function deSerialisedCompanyType(companyType) {
    const deserialiseCompanyTypes = {
        limited_liability_company: 'ltd',
        limited_liability_partnership: 'llp',
        sole_trader: 'st',
        ordinary_partnership: 'op',
        public_limited_company: 'plc'
    };

    return deserialiseCompanyTypes[companyType] || companyType;
}

class LimitedCompanyFields extends React.Component {
    onCompanySelect = (item) => {
        const {
            fields: { company },
            resetDirectors,
            updateBusinessType
        } = this.props;
        const {
            label: companyName,
            value: companyNumber,
            registration_date: companyRegistrationDate,
            company_type: companyType
        } = item;
        const {
            month: companyRegistrationMonth = '',
            year: companyRegistrationYear = ''
        } = splitISODate(companyRegistrationDate) || {};

        updateBusinessType(deSerialisedCompanyType(companyType));

        company.company_name.onChange(companyName);
        company.company_number.onChange(companyNumber);
        company.registered.mm.onChange(companyRegistrationMonth);
        company.registered.yy.onChange(companyRegistrationYear);
        tracking.ampli.companyNameSearched();
        resetDirectors();
    };

    onCompanyNameChange = (input) => {
        this.props.fetchCompanies(input);
    };

    customRenderCompanies = (option) => {
        return (
            <span>
                {option.label} <span className="helper">{option.value}</span>
            </span>
        );
    };

    onFieldsToggle = (e) => {
        e.preventDefault();
        this.props.toggleManualCompanyName();
    };

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            fields: { company },
            values: {
                company: { registered, annual_turnover, monthly_turnover }
            },
            isInSplitTestMonthlyRevenue
        } = nextProps;

        // 'registered' is the company registration date
        if (getAgeFromDateInMonths(registered) < 0) {
            if (isInSplitTestMonthlyRevenue) {
                if (monthly_turnover !== 0 || annual_turnover !== 0) {
                    company.monthly_turnover.onChange(0);
                    company.annual_turnover.onChange(0);
                }
            } else {
                if (annual_turnover !== 0) {
                    company.annual_turnover.onChange(0);
                }
            }
        }
    }

    getFinishButtonExplainerText = () => {
        const { intl } = this.props;

        return intl.formatMessage(messages.wontAffectCreditScore);
    };

    renderSearch = ({ intl }) => {
        // eslint-disable-next-line react/display-name
        return ({
            isLoading,
            openSearchMenu,
            closeSearchMenu,
            searchTerm,
            isSelected,
            selectedValue,
            setNewSearchTerm,
            resetSelection
        }) => {
            const repeatSearch = () => {
                // eslint-disable-next-line react/no-find-dom-node
                const companyNameInput = ReactDOM.findDOMNode(
                    this.companySearch
                ).querySelector('input');
                companyNameInput.focus();
                const moveCaretToEndOfInput = (position) =>
                    companyNameInput.setSelectionRange(position, position);
                moveCaretToEndOfInput(companyNameInput.value.length);
                setNewSearchTerm(searchTerm, { getNewOptions: true });
            };

            return (
                <SelectLive
                    ref={(n) => (this.companySearch = n)}
                    isLoading={isLoading}
                    onFocus={() => {
                        tracking.ampli.formFieldStarted({
                            form_field: 'auto_company_search'
                        });
                        openSearchMenu();
                    }}
                    onBlur={() => {
                        closeSearchMenu();
                    }}
                    onChange={(newSearchTerm) => {
                        setNewSearchTerm(newSearchTerm, {
                            getNewOptions: true
                        });
                    }}
                    onKeyDown={(e) => {
                        // Stops enter key causing form errors showing by stopping form submit
                        if (e.which === 13) e.preventDefault();
                        resetSelection();
                    }}
                    onButtonClick={repeatSearch}
                    value={isSelected ? selectedValue : searchTerm}
                    placeholder={`${intl.formatMessage(messages.search)}...`}
                    inputProps={{
                        id: 'company.company_name',
                        name: 'company.company_name',
                        'data-testid': 'company.company_name'
                    }}
                />
            );
        };
    };

    render() {
        const {
            fields: { company },
            ui: { manualCompanyName },
            handleSubmit,
            nextStep,
            fetched,
            companyType,
            values: {
                company: { registered }
            },
            intl,
            isDisabled,
            isBusinessDetailsReductionSplitTest
        } = this.props;

        const companyAgeInMonths = getAgeFromDateInMonths(registered);
        const hasBusinessStarted = companyAgeInMonths >= 0;
        const maskedCompanyNumber = normalizer(
            company.company_number,
            nonAlphaNumeric,
            addLeadingZero
        );
        const doesBusinessHaveValidStartDate = dateFormatter(registered);

        const turnoverSinceTrading =
            doesBusinessHaveValidStartDate &&
            hasBusinessStarted &&
            companyAgeInMonths <= 12;

        const companySearchProps = {
            company: {
                company_name: company.company_name,
                company_number: maskedCompanyNumber
            },
            toggle: this.onFieldsToggle
        };

        const renderFormFields = () => {
            if (isBusinessDetailsReductionSplitTest) {
                return (
                    <div
                        className={classnames(
                            'form-wrapper',
                            styles.businessDetailReductionContainer
                        )}>
                        <FormGroup>
                            <AutoCompany
                                {...companySearchProps}
                                initialSearchTerm={company.company_name.value}
                                renderSearch={this.renderSearch({ intl })}
                                companies={fetched.companies}
                                onSelect={this.onCompanySelect}
                                onCompanyNameChange={this.onCompanyNameChange}
                                companyType={companyType}
                                renderTopMenuElement={() => {}}
                                labelProps={{
                                    htmlFor: 'company.company_name'
                                }}
                            />
                            <TurnoverQuestion
                                annualTurnoverField={company.annual_turnover}
                                monthlyTurnoverField={company.monthly_turnover}
                                disabled={!hasBusinessStarted}
                                turnoverSinceTrading={turnoverSinceTrading}
                            />
                        </FormGroup>
                    </div>
                );
            }

            return (
                <div className="form-wrapper">
                    <FormGroup>
                        <input type="hidden" {...company.type} />
                        {manualCompanyName ? (
                            <ManualCompany {...companySearchProps} />
                        ) : (
                            <AutoCompany
                                {...companySearchProps}
                                initialSearchTerm={company.company_name.value}
                                renderSearch={this.renderSearch({ intl })}
                                companies={fetched.companies}
                                onSelect={this.onCompanySelect}
                                onCompanyNameChange={this.onCompanyNameChange}
                                companyType={companyType}
                                renderTopMenuElement={() => {}}
                                labelProps={{
                                    htmlFor: 'company.company_name'
                                }}
                            />
                        )}
                        <Col xs={12} sm={6}>
                            <BusinessStartDateQuestion
                                monthField={company.registered.mm}
                                yearField={company.registered.yy}
                            />
                        </Col>
                    </FormGroup>
                    <FormGroup>
                        <Col xs={12} sm={6}>
                            <VATQuestion
                                field={company.vat_registered_status}
                            />
                        </Col>
                        <TurnoverQuestion
                            annualTurnoverField={company.annual_turnover}
                            monthlyTurnoverField={company.monthly_turnover}
                            disabled={!hasBusinessStarted}
                            turnoverSinceTrading={turnoverSinceTrading}
                        />
                    </FormGroup>
                    <FormGroup>
                        <Col xs={12} sm={6}>
                            <WebsiteQuestion field={company.website} />
                        </Col>
                    </FormGroup>
                </div>
            );
        };

        return (
            <form
                data-testid="businessIntroductionForm"
                noValidate
                autoComplete="off"
                className={classnames('form-horizontal', {
                    [styles.disabledFormDisplay]: isDisabled
                })}
                onSubmit={handleSubmit(nextStep)}>
                {/* TODO: Remove this disabled block element */}
                {isDisabled ? (
                    <div
                        data-testid="form-disabler"
                        className={styles.disableForm}></div>
                ) : null}
                {renderFormFields()}

                <FormattedMessage {...messages.continueButton}>
                    {(txt) => (
                        <>
                            <FinishStepButton
                                type="submit"
                                onClick={() =>
                                    scrollToErrors(this.props.errors)
                                }
                                explainerText={this.getFinishButtonExplainerText()}>
                                {txt}
                            </FinishStepButton>
                            <TrustPilotBadge />
                        </>
                    )}
                </FormattedMessage>
            </form>
        );
    }
}

const intlLimitedCompanyFields = injectIntl(LimitedCompanyFields);

export default reduxForm(
    {
        form: 'sign_up',
        fields: [...limitedCompanyBusinessTypeFields],
        destroyOnUnmount: false,
        asyncBlurFields: [...limitedCompanyBusinessTypeFields],
        alwaysAsyncValidate: true,
        getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
        asyncValidate: (values, dispatch, props) => {
            const {
                isInSplitTestMonthlyRevenue,
                isDirectUKCustomer,
                isBusinessDetailsReductionSplitTest
            } = props;

            const schema = businessDetailsFormValidation({
                monthlyTurnoverOptional: isInSplitTestMonthlyRevenue,
                vatOptional: isDirectUKCustomer
                    ? isBusinessDetailsReductionSplitTest
                    : !isDirectUKCustomer
            });
            return schemaValidator(schema)(values, dispatch, props);
        },
        enableReinitialize: true
    },
    (state) => {
        return {
            initialValues: {
                ...state.fetched.application.payload
            },
            ui: state.ui,
            fetched: state.fetched,
            hasAutomatedDecision: hasAutomatedDecisionSelector(state),
            isInSplitTestMonthlyRevenue: getSplitTestValue(
                state,
                'monthly_revenue_question'
            ),
            isDirectUKCustomer: isDirectUKCustomer(state),
            isBusinessDetailsReductionSplitTest:
                getSplitTestValue(state, 'business_details_reduction') &&
                getCountryCode(state) === 'uk'
        };
    },
    (dispatch) => ({
        fetchCompanies: (companyName) =>
            dispatch({ type: 'COMPANIES_FETCH_REQUESTED', companyName }),
        toggleManualCompanyName: (index) =>
            dispatch(toggleManualCompanyName(index)),
        resetDirectors: () => dispatch(resetDirectors()),
        updateBusinessType: (companyType) =>
            dispatch({
                type: 'redux-form/CHANGE',
                field: 'company.type',
                value: companyType,
                touch: false,
                form: 'sign_up',
                key: 'businessType'
            })
    })
)(intlLimitedCompanyFields);
